import { conditions } from "../../helpers/conditions/conditions";
import { handleHasXTokenParamOrSendXToken, responseValidator } from "../../helpers/helpers";
import { ConditionName, QuizLoginSteps, UserQuizStatus } from "../../interfaces/enums";
import {
    IQuizQuestions,
    IUserQuestions,
    IQuizUserRecords,
    QuizType,
    IQuizChoice,
    IQuizGenerateCode,
    IQuizLoginOtp
} from "../../interfaces/interfaces";
import { apiCaller } from "../../services/apiCaller";
import { API } from "../../services/services";
import store from "../store";
import { setChances, setMaxChances, setUserAnswerId, setUserQuestionId, setUserQuestions, setUserStatus } from "../slices/quizSlice";
import { toast } from "mobin-toast";
import { otpType } from "helpers";
import Cookies from "js-cookie";
import { setUserInfo } from "../slices/userSlice";
import { setPlayerParams } from "../slices/videoDataSlice";
import { setLoginPopover } from "../slices/popoversSlice";

export const handleSelectedAnswer = async (answerId: number, setShowSpinner: (value: undefined | number) => void) => {
    setShowSpinner(answerId);
    const dispatch = store.dispatch;
    const { quiz } = store.getState().quiz;
    const answer = {
        quiz: {
            id: quiz.id,
            questions: [{ id: lastQuizQuestionId(quiz), choices: [{ id: answerId }] }]
        },
        encrypted_quiz: quiz.encrypted_quiz
    };
    const { data, status } = await apiCaller({ url: API().live.quiz, method: "POST", data: answer, headers: handleHasXTokenParamOrSendXToken() });
    if (responseValidator(status)) dispatch(setUserAnswerId(answerId));
    else if (data.detail) toast({ type: "error", title: data.detail });
    else toast({ type: "error", title: "خطای سرور در ثبت پاسخ" });
    setShowSpinner(undefined);
};
export const firstQuizQuestion = (quiz: QuizType) => quiz.questions[0];
export const firstQuizQuestionId = (quiz: QuizType) => firstQuizQuestion(quiz).id;

export const lastQuizQuestion = (quiz: QuizType, general?: boolean) =>
    !!general ? quiz.questions[quiz.number_of_questions - 1] : quiz.questions[quiz.questions.length - 1];
export const lastQuizQuestionId = (quiz: QuizType, general?: boolean) => {
    const result = lastQuizQuestion(quiz, general);
    if (result) return result.id;
    else return 0;
};
export const lastQuizQuestionStatus = (quiz: QuizType) => (quiz.questions.length ? lastQuizQuestion(quiz).status : undefined);
export const lastQuizQuestionChoices = (quiz: QuizType) => lastQuizQuestion(quiz).choices;
export const lastQuizQuestionOrder = (quiz: QuizType) => lastQuizQuestion(quiz).order;
export const lastQuizQuestionText = (quiz: QuizType) => lastQuizQuestion(quiz).text;
export const findParticipantsInEliminationQuiz = (quiz: QuizType) =>
    firstQuizQuestion(quiz).choices.find((choice: any) => choice.is_correct === true)!.participants;
export const findCorrectQuizAnswer = (quiz: QuizType) => lastQuizQuestion(quiz).choices.find((choice: any) => choice.is_correct === true)!;

export const handleUserStatus = (chance?: number) => {
    if (!conditions(ConditionName.isLogin)) return UserQuizStatus.viewer;
    else if (conditions(ConditionName.isPointsQuiz)) return UserQuizStatus.participant;
    else return chance !== undefined && chance > 0 ? UserQuizStatus.participant : UserQuizStatus.loser;
};
export const handleCurrentQuestion = (data: IQuizUserRecords) => {
    const dispatch = store.dispatch;
    const { quiz } = store.getState().quiz;
    const currentQuestionIdInAggregate = handleCurrentQuestionInAggregate(quiz).id;
    const currentUserQuestion = data.question.find((item: IUserQuestions) => +item.id === currentQuestionIdInAggregate);
    dispatch(setUserQuestionId(currentUserQuestion ? +currentUserQuestion.id : 0));
    return currentUserQuestion ?? {};
};

export const handleCurrentQuestionInAggregate = (quiz: QuizType) => quiz.questions[quiz.questions.length - 1];

export const handleUserAnswerId = (data: IQuizUserRecords) => {
    const question: any = data.question ? handleCurrentQuestion(data) : {};
    if (Object.keys(question).length) return +question.choice.id;
    else return 0;
};

export const handleCorrectAnswer = () => {
    const { quiz } = store.getState().quiz;
    return handleCurrentQuestionInAggregate(quiz).choices.find((choice: any) => choice.is_correct === true)!;
};

export const handleUserAnswersCount = () => {
    const { userQuestions } = store.getState().quiz;
    return userQuestions.length;
};

export const handleUserCorrectAnswersCount = (userQuestions: IUserQuestions[]) => {
    const { quiz } = store.getState().quiz;
    let userCorrectAnswerCounts = 0;
    if (userQuestions.length) {
        quiz.questions.map((question: IQuizQuestions) => {
            const correctChoice = question.choices.find((choice: IQuizChoice) => choice.is_correct === true);
            if (correctChoice && userQuestions.find((question: IUserQuestions) => +question.choice.id === correctChoice.id))
                userCorrectAnswerCounts += 1;
        });
    }
    return userCorrectAnswerCounts;
};

export const isUserAnswerFirstQuestion = () => {
    const { quiz, userQuestions } = store.getState().quiz;
    const isUserAnswerFirstQuestion = userQuestions.find((question: IUserQuestions) => +question.id === quiz.questions[0].id);
    return !!isUserAnswerFirstQuestion;
};

export const isUserAnswerLastQuestion = () => {
    const { quiz, userQuestions } = store.getState().quiz;
    const isUserAnswerLastQuestion =
        !!quiz.questions[quiz.number_of_questions - 1] &&
        userQuestions.find((question: IUserQuestions) => +question.id === quiz.questions[quiz.number_of_questions - 1].id);
    return !!isUserAnswerLastQuestion;
};

export const handleUserRecordesQuiz = async (quiz: any) => {
    const dispatch = store.dispatch;
    const { data, status } = await apiCaller({
        url: API().live.quizUserRecoreds(quiz.type, quiz.id),
        method: "POST",
        data: { encrypted_quiz: quiz.encrypted_quiz },
        headers: handleHasXTokenParamOrSendXToken()
    });
    if (responseValidator(status) && data && Object.keys(data).length) {
        dispatch(setUserAnswerId(handleUserAnswerId(data)));
        if (conditions(ConditionName.isPointsQuiz)) {
            dispatch(setUserStatus(handleUserStatus()));
            if (data.question) dispatch(setUserQuestions(data.question));
        } else {
            dispatch(setMaxChances(data.max_chance));
            dispatch(setChances(data.chance + 1));
            dispatch(setUserStatus(handleUserStatus(data.chance + 1)));
        }
    }
};

export const handleQuizParticipantsPerQuestion = () => {
    const { quiz } = store.getState().quiz;
    let participantsCount = 0;
    lastQuizQuestionChoices(quiz).map((choice: IQuizChoice) => (participantsCount += choice.participants));
    return participantsCount;
};

// quiz login actions
const handleValidationForm = (mobileNumber: string) => {
    let isValidate = true;
    if (!mobileNumber || mobileNumber.length < 11 || !new RegExp("^(?:(0))?((?:90|91|92|93|99)[0-9]{8})$").test(mobileNumber)) isValidate = false;
    return isValidate;
};

export const handleGenerateCode = async ({
    event,
    quizFormData,
    setQuizFormData,
    setLoading,
    setMobileNumberError,
    setResendCodeTime,
    setLoginStep
}: IQuizGenerateCode) => {
    if (event) event.preventDefault();
    if (handleValidationForm(quizFormData.mobileNumber)) {
        setLoading(true);
        const data = {
            mobileNumber: quizFormData.mobileNumber,
            otpType: otpType
        };
        setLoginStep(QuizLoginSteps.otp);
        const response = await apiCaller({ url: API().auth.loginNasle3(), method: "POST", data });
        setLoading(false);
        if (response) {
            if (responseValidator(response.status)) {
                if (setResendCodeTime) setResendCodeTime(120);
                setQuizFormData({ ...quizFormData, requestId: response.data.data });
                localStorage.setItem(
                    "quiz-otp-info",
                    JSON.stringify({ requestId: response.data.data, expiredTime: new Date().getTime() / 1000 + 120 })
                );
                setLoginStep(QuizLoginSteps.otp);
            } else if (response.status === 400) {
                const quizOtpInfo = localStorage.getItem("quiz-otp-info");
                if (quizOtpInfo) {
                    setLoginStep(QuizLoginSteps.otp);
                    const parsedQuizOtpInfo = JSON.parse(quizOtpInfo);
                    setQuizFormData({ ...quizFormData, requestId: parsedQuizOtpInfo.requestId });
                    const remainingTime = parsedQuizOtpInfo.expiredTime - new Date().getTime() / 1000;
                    if (setResendCodeTime) setResendCodeTime(remainingTime > 0 ? remainingTime : 0);
                }
            } else toast({ title: "مشکلی پیش آمده است. لطفا دوباره تلاش کنید", type: "error" });
        } else toast({ title: "مشکلی پیش آمده است. لطفا دوباره تلاش کنید", type: "error" });
    } else if (setMobileNumberError) setMobileNumberError(true);
};

export const handleOtp = async ({ event, otpCode, quizFormData, setLoading }: IQuizLoginOtp) => {
    const dispatch = store.dispatch;
    const { params } = store.getState().videoData;
    if (event) event.preventDefault();
    const data = {
        key: otpCode,
        requestId: quizFormData.requestId,
        otpType: otpType
    };
    const response = await apiCaller({ url: API().auth.verifyOtp(), method: "POST", data });
    if (response) {
        if (responseValidator(response.status)) {
            Cookies.set("xToken", response.data.token, { sameSite: "none", secure: true });
            setLoading(false);
            const user = {
                name: response.data.profile.displayName,
                avatar_url: response.data.profile.image,
                phone_number: response.data.profile.phoneNumber,
                id: response.data.profile.id
            };
            dispatch(setUserInfo(user));
            dispatch(setUserStatus(UserQuizStatus.participant));
            dispatch(setPlayerParams({ ...params, xToken: response.data.token }));
            dispatch(setLoginPopover(false));
            const { quiz } = store.getState().quiz;
            if (conditions(ConditionName.hasQuiz)) handleUserRecordesQuiz(quiz);
        } else if (response.status === 400) {
            toast({ title: "کد وارد شده اشتباه است.", type: "error" });
        }
    } else {
        setLoading(false);
        toast({ title: "مشکلی پیش آمده است. دوباره تلاش کنید", type: "error" });
    }
};
